import { useEffect, useRef, useState } from "react";
import { Dictionary, ValidationResultItem } from "@/types/types";
import { useDictCreation } from "@/hooks/useDictCreation";
import { ClickEvent } from "devextreme/ui/button";
import { closePopup, reloadGrid } from "@/helper/DxHelper";
import {
  Button,
  CheckBox,
  LoadIndicator,
  Lookup,
  Popup,
  TextArea,
  TextBox,
  ValidationSummary,
  Validator,
} from "devextreme-react";
import ServerResponse from "@/components/ServerResponse";
import { AxiosError } from "axios";
import { RequiredRule } from "devextreme-react/validator";
import { DictionaryLookupDS } from "@/dataSources/DictionaryLookupDS";
import { useDictionaryTypes } from "@/hooks/useDictionaryTypes";
import { PopupRef } from "devextreme-react/cjs/popup";
import { DataGridRef } from "devextreme-react/cjs/data-grid";
import { TextBoxRef } from "devextreme-react/cjs/text-box";
import { CheckBoxRef } from "devextreme-react/cjs/check-box";
import { LookupRef } from "devextreme-react/cjs/lookup";
import { TextAreaRef } from "devextreme-react/cjs/text-area";

type Props = {
  popupRef: React.RefObject<PopupRef>;
  datagridRef: React.RefObject<DataGridRef>;
};

const validationGroupName = "insertDictVal";

export default function Insert({ datagridRef, popupRef }: Props) {
  const [serverErrors, setServerErrors] = useState<ValidationResultItem[] | null>(null);
  const { mutate, data: response, isPending, isError, error, isSuccess } = useDictCreation();
  const { data: dictionaryTypes } = useDictionaryTypes();

  const nameRef = useRef<TextBoxRef>(null);
  const fullnameRef = useRef<TextBoxRef>(null);
  const inactiveRef = useRef<CheckBoxRef>(null);
  const parentRef = useRef<LookupRef>(null);
  const valueRef = useRef<TextBoxRef>(null);
  const codeRef = useRef<TextBoxRef>(null);
  const commentRef = useRef<TextAreaRef>(null);
  const typeRef = useRef<LookupRef>(null);

  function submit(e: ClickEvent) {
    setServerErrors(null);

    if (!e.validationGroup.validate().isValid) return;

    const requestData: Partial<Dictionary> = {
      name: nameRef.current?.instance().option("value"),
      fullname: fullnameRef.current?.instance().option("value"),
      inactive: inactiveRef.current?.instance().option("value"),
      parent: parentRef.current?.instance().option("value"),
      value: valueRef.current?.instance().option("value"),
      code: codeRef.current?.instance().option("value"),
      comment: commentRef.current?.instance().option("value"),
      type: typeRef.current?.instance().option("value")?.key,
    };

    mutate(requestData);
  }

  function disableInputs(state: boolean) {
    nameRef.current?.instance().option("disabled", state);
    fullnameRef.current?.instance().option("disabled", state);
    inactiveRef.current?.instance().option("disabled", state);
    parentRef.current?.instance().option("disabled", state);
    valueRef.current?.instance().option("disabled", state);
    codeRef.current?.instance().option("disabled", state);
    commentRef.current?.instance().option("disabled", state);
    typeRef.current?.instance().option("disabled", state);
  }

  function clearForm() {
    nameRef?.current?.instance().option("value", "");
    fullnameRef?.current?.instance().option("value", "");
    inactiveRef?.current?.instance().option("value", false);
    parentRef?.current?.instance().option("value", null);
    valueRef?.current?.instance().option("value", "");
    codeRef?.current?.instance().option("value", "");
    commentRef?.current?.instance().option("value", "");
    typeRef?.current?.instance().option("value", null);
  }

  useEffect(() => {
    if (isSuccess) {
      if (!response?.validation.items?.length) {
        clearForm();
        closePopup(popupRef);
      } else {
        setServerErrors(response?.validation.items);
      }
    }
  }, [isSuccess, popupRef, response?.validation.items]);

  useEffect(() => {
    disableInputs(isPending);
  }, [isPending]);

  return (
    <Popup
      title="Add dictionary entry"
      showCloseButton={true}
      ref={popupRef}
      onHiding={() => {
        clearForm();
        setServerErrors(null);
        reloadGrid(datagridRef);
      }}
    >
      <form className="mx-1 grid grid-cols-2 gap-8">
        <TextBox label="Name" ref={nameRef} stylingMode="outlined" valueChangeEvent="keyup">
          <Validator validationGroup={validationGroupName}>
            <RequiredRule message={"Name is required"} />
          </Validator>
        </TextBox>
        <TextBox
          label="Full Name"
          ref={fullnameRef}
          stylingMode="outlined"
          labelMode="static"
          valueChangeEvent="keyup"
        />
        <TextBox
          labelMode="static"
          label="Value"
          ref={valueRef}
          stylingMode="outlined"
          valueChangeEvent="keyup"
        />
        <TextBox
          labelMode="static"
          label="Code"
          ref={codeRef}
          stylingMode="outlined"
          valueChangeEvent="keyup"
        />
        <Lookup
          label="Parent"
          displayExpr="name"
          dataSource={DictionaryLookupDS(undefined)}
          stylingMode="outlined"
          labelMode="static"
          ref={parentRef}
          searchEnabled={true}
          showClearButton={true}
        />
        <Lookup
          searchEnabled={true}
          label="Type"
          items={dictionaryTypes ?? []}
          displayExpr={"description"}
          stylingMode="outlined"
          labelMode="static"
          ref={typeRef}
        >
          <Validator validationGroup={validationGroupName}>
            <RequiredRule message={"Type is required"} />
          </Validator>
        </Lookup>

        <TextArea
          label="Comment"
          ref={commentRef}
          stylingMode="outlined"
          labelMode="static"
          valueChangeEvent="keyup"
        />

        <div className="col-start-2 mt-4">
          <CheckBox ref={inactiveRef} text="Inactive" />
        </div>
      </form>
      <div className="absolute bottom-4 left-4">
        <div className="text-danger">
          {isError && error instanceof AxiosError ? <span>{error.message}</span> : null}
        </div>
        <ValidationSummary validationGroup={validationGroupName} id="summary" />
        {serverErrors?.length ? <ServerResponse items={serverErrors} /> : null}
      </div>
      <div className="absolute bottom-4 right-4">
        <Button
          validationGroup={validationGroupName}
          useSubmitBehavior={false}
          type="default"
          height={40}
          width={isPending ? 150 : 100}
          onClick={submit}
        >
          <LoadIndicator
            id="default-btn-loading"
            className="mr-3 inline-block align-middle"
            width={25}
            height={25}
            visible={isPending}
          />
          <span className="dx-button-text">{isPending ? "Creating" : "Create"}</span>
        </Button>
      </div>
    </Popup>
  );
}
